/**
 * The VOCABULARY slice of the admin surface — platforms (migration 38, owner ruling
 * Q8/Q8-b) and stacks (migration 39, D-2b), plus the per-country platform flags (D-2).
 *
 * A FIFTH api file, for the reason `tax-groups-api.ts` is a fourth: one owner per
 * file, and DTOs transcribed HERE rather than imported from `features/crfs/api.ts` —
 * that module's `PlatformOptionDto` exists for the PICKER, which is a different route
 * with a different gate (`GET /countries/:cc/crfs/platforms`, `crf.view`), and this
 * file's screens are the MAINTENANCE views (`config.global` / `config.manage`) that
 * show retired values the picker deliberately hides.
 *
 * THREE SURFACES, TWO PERMISSION DOMAINS — mirroring `platforms.controller.ts:9-12`:
 *
 *   `/admin/platforms`          `config.global`  group-wide   (PlatformsPage)
 *   `/admin/stacks`             `config.global`  group-wide   (StacksPage)
 *   `/countries/:cc/platforms`  `config.manage`  one country  (PlatformAvailabilityPage)
 *
 * There is deliberately NO `deleteX` function anywhere in this file: neither
 * controller has a DELETE route (`platforms.controller.ts:40-45`,
 * `stacks.controller.ts:34-39`) — `active: false` IS retire, and the attach tables
 * carry `ON DELETE RESTRICT`. A client function for a route that does not exist is a
 * button that can only ever 404.
 *
 * And NO country surface for stacks: `/countries/:cc/stacks` does not exist and must
 * not (D-2b — a country does not choose what our engineers build in;
 * `stacks.controller.ts:9-20`, pinned by `stacks.e2e-spec.ts` suite (a)).
 *
 * Every call goes through `apiRequest` (single-flight refresh mutex, D-web-1).
 */

import { apiRequest } from '../../lib/api';
import type { CountryCode } from '../../domain/countries';

/**
 * The SET's members — what `applies_to` may draw from, transcribed from
 * `platform.entity.ts:40`. This is the DOMAIN of the answer (schema); the answer
 * itself is data on each row, and nothing in this app maps a platform name to an
 * applicability (owner ruling Q8-b).
 */
export const PLATFORM_TARGETS = ['crf', 'project', 'inquiry'] as const;
export type PlatformTarget = (typeof PLATFORM_TARGETS)[number];

/** `PlatformAdminDto` — transcribed from `platforms.service.ts:113`. */
export interface PlatformAdminRow {
  id: string;
  name: string;
  sort_order: number;
  active: boolean;
  /** A LIST since migration 48 — the SET members this row carries, declaration order. */
  applies_to: string[];
  /** Records of `work_type='crf'` carrying this value right now. */
  attached_crf: number;
  /** Records of `work_type='project'` carrying this value right now. */
  attached_project: number;
  /** Countries that have recorded a DELIBERATE answer for this value (either way). */
  country_decisions: number;
}

/** `CountryPlatformDto` — transcribed from `platforms.service.ts:140`. */
export interface CountryPlatformRow {
  id: string;
  name: string;
  sort_order: number;
  applies_to: string[];
  /** The group-wide switch. `false` ⇒ not offered anywhere, whatever this country says. */
  group_active: boolean;
  /** This country's effective answer — absence means offered (D-2). */
  active: boolean;
  /** `true` when a `country_platforms` row records a deliberate decision. */
  explicit: boolean;
  /** Whether the picker in THIS country will offer it: `group_active && active`. */
  offered: boolean;
}

/** `StackAdminDto` — transcribed from `stacks.service.ts:78`. */
export interface StackAdminRow {
  id: string;
  name: string;
  sort_order: number;
  active: boolean;
  /**
   * Projects carrying this value right now. ONE count, not two — a stack cannot
   * legally attach to a CRF (`ck_crf_stacks_project`), so a CRF half would imply a
   * distinction that cannot exist (`stacks.service.ts:73-77`).
   */
  attached: number;
}

/* ─────────────────────────────────────────────── group-wide · config.global ── */

export async function fetchAdminPlatforms(signal?: AbortSignal): Promise<PlatformAdminRow[]> {
  const body = await apiRequest<{ data: PlatformAdminRow[] }>('/admin/platforms', { signal });
  return body.data;
}

export async function createPlatform(body: {
  name: string;
  applies_to: string[];
  sort_order?: number;
  active?: boolean;
}): Promise<PlatformAdminRow> {
  return apiRequest<PlatformAdminRow>('/admin/platforms', { method: 'POST', body });
}

/**
 * Every field optional, absent means untouched (`UpdatePlatformDto`). A narrowing of
 * `applies_to` that would strand attached rows is refused BY THE SERVER, naming the
 * count — the screen surfaces that sentence verbatim and holds no strand-check of
 * its own (the affordance/guard split, `crfs/api.ts:341`).
 */
export async function patchPlatform(
  id: string,
  body: { name?: string; applies_to?: string[]; sort_order?: number; active?: boolean },
): Promise<PlatformAdminRow> {
  return apiRequest<PlatformAdminRow>(`/admin/platforms/${id}`, { method: 'PATCH', body });
}

export async function fetchAdminStacks(signal?: AbortSignal): Promise<StackAdminRow[]> {
  const body = await apiRequest<{ data: StackAdminRow[] }>('/admin/stacks', { signal });
  return body.data;
}

export async function createStack(body: {
  name: string;
  sort_order?: number;
  active?: boolean;
}): Promise<StackAdminRow> {
  return apiRequest<StackAdminRow>('/admin/stacks', { method: 'POST', body });
}

export async function patchStack(
  id: string,
  body: { name?: string; sort_order?: number; active?: boolean },
): Promise<StackAdminRow> {
  return apiRequest<StackAdminRow>(`/admin/stacks/${id}`, { method: 'PATCH', body });
}

/* ─────────────────────────────────────────── one country · config.manage ── */

export async function fetchCountryPlatforms(
  countryCode: CountryCode,
  signal?: AbortSignal,
): Promise<CountryPlatformRow[]> {
  const body = await apiRequest<{ data: CountryPlatformRow[] }>(
    `/countries/${countryCode}/platforms`,
    { signal },
  );
  return body.data;
}

/**
 * UPSERT of the listed values only — never a destructive replace
 * (`PutCountryPlatformsDto`, `platform.dto.ts:212-219`). `active` is REQUIRED on
 * every item because absence already means something on this table (D-2: no row =
 * offered), so the client always says which way the switch goes.
 */
export async function putCountryPlatforms(
  countryCode: CountryCode,
  items: Array<{ platform_id: string; active: boolean }>,
): Promise<CountryPlatformRow[]> {
  const body = await apiRequest<{ data: CountryPlatformRow[] }>(
    `/countries/${countryCode}/platforms`,
    { method: 'PUT', body: { items } },
  );
  return body.data;
}

/* ─────────────────────────────────────────────────────────────── query keys ── */

export const vocabularyKeys = {
  platforms: () => ['admin-platforms'] as const,
  stacks: () => ['admin-stacks'] as const,
  countryPlatforms: (cc: CountryCode) => ['country-platforms', cc] as const,
};
